import { generateToDocFn } from '@dadohr/shared/domainTypes/document';
import {
  IAITermsVersion,
  IOrgSettings,
  IOrgSettingsLog,
  IOrgTosAcceptance,
  TosType
} from '@dadohr/shared/domainTypes/orgSettings';
import { FS } from '@dadohr/shared/versions';
import { getCurrentUser } from '../currentUser';
import { setInTransaction, store } from '../firebase';
import {
  createDocumentListenerGetter,
  useDocumentListener
} from '../firecache/documentListener';
import { now } from '../time';

const normalizeFn = (t: IOrgSettings) => t;
export const toOrgSettingsDoc = generateToDocFn<IOrgSettings>(normalizeFn);

const EMPTY_DEFAULT = (orgId: string): IOrgSettings => ({
  id: orgId,
  orgId,
  createdAt: now()
});

const orgSettingsCollection = () => store().collection(FS.orgSettings);
const orgSettingsLogCollection = () => store().collection(FS.orgSettingsLog);
const orgTosAcceptancesCollection = () =>
  store().collection(FS.orgTosAcceptances);

export const orgSettingsStore = createDocumentListenerGetter<IOrgSettings>(
  (id) => orgSettingsCollection().doc(id),
  toOrgSettingsDoc,
  EMPTY_DEFAULT
);

export const useOrgSettings = (orgId: string) => {
  return useDocumentListener(orgSettingsStore(orgId));
};

export const setAIEnabled = async (orgId: string, enabled: boolean) => {
  const currentUser = getCurrentUser();
  const timestamp = now();

  return setInTransaction<IOrgSettings>(
    orgSettingsCollection().doc(orgId),
    async (d) => {
      const existing = d || EMPTY_DEFAULT(orgId);
      const previousValue = existing.ai?.enabled ?? false;

      // Log the change
      const logEntry: Omit<IOrgSettingsLog, 'id'> = {
        orgId,
        setting: 'ai.enabled',
        previousValue,
        newValue: enabled,
        changedAt: timestamp,
        changedBy: currentUser.id,
        changedByName: `${currentUser.firstName} ${currentUser.lastName}`,
        createdAt: timestamp
      };
      await orgSettingsLogCollection().add(logEntry);

      return {
        ...existing,
        ai: {
          ...existing.ai,
          enabled,
          updatedAt: timestamp,
          updatedBy: currentUser.id
        },
        updatedAt: timestamp
      };
    },
    normalizeFn
  );
};

export const acceptTos = async (
  orgId: string,
  tosType: TosType,
  tosVersion: string
) => {
  const currentUser = getCurrentUser();
  const timestamp = now();

  // Log the acceptance
  const acceptance: Omit<IOrgTosAcceptance, 'id'> = {
    orgId,
    tosType,
    tosVersion,
    acceptedAt: timestamp,
    acceptedBy: currentUser.id,
    acceptedByName: `${currentUser.firstName} ${currentUser.lastName}`,
    acceptedByEmail: currentUser.email,
    createdAt: timestamp
  };
  await orgTosAcceptancesCollection().add(acceptance);

  // Update orgSettings with TOS acceptance
  if (tosType === 'ai') {
    await setInTransaction<IOrgSettings>(
      orgSettingsCollection().doc(orgId),
      async (d) => {
        const existing = d || EMPTY_DEFAULT(orgId);
        return {
          ...existing,
          ai: {
            ...existing.ai,
            enabled: existing.ai?.enabled ?? false,
            tosAcceptedVersion: tosVersion,
            tosAcceptedAt: timestamp,
            tosAcceptedBy: currentUser.id,
            tosAcceptedByName: `${currentUser.firstName} ${currentUser.lastName}`,
            updatedAt: timestamp,
            updatedBy: currentUser.id
          },
          updatedAt: timestamp
        };
      },
      normalizeFn
    );
  }
};

// AI Terms Versions
const aiTermsVersionsCollection = () =>
  store().collection(FS.aiTermsVersions);

const toAITermsVersionDoc = generateToDocFn<IAITermsVersion>((t) => t);

const aiTermsVersionStore = createDocumentListenerGetter<IAITermsVersion>(
  (id) => aiTermsVersionsCollection().doc(id),
  toAITermsVersionDoc
);

export const useAITermsVersion = (versionId: string) => {
  return useDocumentListener(aiTermsVersionStore(versionId));
};

/**
 * Hook to check if AI features are enabled for an organization.
 * Returns true only if BOTH:
 * 1. The AI_V1 feature flag is enabled for the org
 * 2. The org has enabled AI features in their settings (after accepting TOS)
 */
export const useIsOrgAIEnabled = (orgId: string) => {
  const [orgSettings] = useOrgSettings(orgId);
  return orgSettings?.data?.ai?.enabled ?? false;
};
